import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useBlocker } from 'react-router-dom';
import { fetchEmployees, fetchCustomers, postCustomerOrder } from "../../Utils/api";
import { useAuth } from "../../Auth/AuthContext";
import Switch from '@mui/material/Switch';

import {
    TextField,
    InputAdornment,
    Button,
    Grid,
    Autocomplete,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    Typography,
    Box,
    Tooltip,
    FormControlLabel,
    Breadcrumbs,
    Link,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import EventIcon from "@mui/icons-material/Event";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import dayjs from "dayjs";
import PopupGeneric from "../../Components/PopupGeneric/pop-up-generic"; // Import the PopupGeneric component
import { validateAddress, validateCityState, validateName, validatePersonDetails, validatePhone, validatePoolDetails, validateWorkOrderDetails, validateZip, validateEmail, normalizePhoneDigits } from "../../Utils/validations";
import { US_STATES } from "../../Utils/constants";

const STATE_ABBREVS = {
    Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
    Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
    Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
    Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD",
    Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO",
    Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
    "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
    Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI",
    "South Carolina": "SC", "South Dakota": "SD", Tennessee: "TN", Texas: "TX",
    Utah: "UT", Vermont: "VT", Virginia: "VA", Washington: "WA", "West Virginia": "WV",
    Wisconsin: "WI", Wyoming: "WY",
};

const AddCustomer = () => {
    const poolTypesDD = [
        { value: "RESIDENTIAL", label: "Residential" },
        { value: "COMMERCIAL", label: "Commercial" },
        { value: "POOL", label: "Pool" },
        { value: "POOLSPA", label: "Pool+Spa" },
    ];
    const taskOptions = [
        "Regular Service",
        "Back-wash",
        "Filter breakdown",
        "On Demand",
    ];
    const navigate = useNavigate();

    const [step, setStep] = useState(1);
    const [isNavigationBlockerVisible, setNavigationBlockerVisible] = useState(false);
    const [blockedNavigation, setBlockedNavigation] = useState(null);
    const hasUnsavedDataRef = useRef(false);
    const [technicianList, setTechnicianList] = useState([]);
    const [selectedTechnician, setSelectedTechnician] = useState("");
    const [selectedTask, setSelectedTask] = useState("");
    const [isNextDisabled, setIsNextDisabled] = useState(true);
    const [additionalComments, setAdditionalComments] = useState("");
    const [occurrence, setOccurrence] = useState("");
    const [date, setDate] = useState("");
    const [showPoolRequired, setShowPoolRequired] = useState(false);
    const [showWorkOrderRequired, setShowWorkOrderRequired] = useState(false);
    const [showCommentsRequired, setShowCommentsRequired] = useState(false);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // State for popup
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [popupTitle, setPopupTitle] = useState("");
    const [popupMessage, setPopupMessage] = useState("");
    const { companyId } = useAuth();
    const [existingEmployees, setExistingEmployees] = useState([]);
    const [existingCustomers, setExistingCustomers] = useState([]);
    const [emailError, setEmailError] = useState("");
    const [secondaryEmailError, setSecondaryEmailError] = useState("");
    const [sameEmailError, setSameEmailError] = useState("");

    // Ref for date input field
    const dateInputRef = React.useRef(null);

    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        phoneNumber: "",
        address: "",
        city: "",
        state: "",
        zipCode: "",
        email: "",
        secondaryEmail: "",
        status: "Y",
        poolType: "",
        length: "",
        width: "",
        height: "",
        shape: "",
        dayOfWeek: "",
        recurrence: "",
    });

    const [volume, setVolume] = useState(null);

    const MAX_COMMENT_LENGTH = 1000;

    // Scroll to top on mount
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Track unsaved data via ref so the blocker never reads a stale closure
    useEffect(() => {
        const anyFieldFilled = formData.firstName !== "" || formData.lastName !== "" ||
            formData.phoneNumber !== "" || formData.address !== "";
        hasUnsavedDataRef.current = step > 1 || anyFieldFilled;
    }, [step, formData]);

    const blocker = useBlocker(({ currentLocation, nextLocation }) =>
        hasUnsavedDataRef.current &&
        currentLocation.pathname !== nextLocation.pathname
    );

    useEffect(() => {
        if (blocker.state === "blocked") {
            setBlockedNavigation(blocker);
            setNavigationBlockerVisible(true);
        }
    }, [blocker.state]);

    // Handle form submission
    const handleSubmit = async () => {
        setIsSubmitting(true);
        const occurrenceMap = {
            "One time": 0,         
            "Daily": 1,
            "Weekly": 7,
            "Every other week": 14,
            "Once a month": 30,
            "Quarterly": 90,
            "Twice a year": 180,
            "Yearly": 365
          };
        try {
          const isPoolDetailsFilled = Boolean(
            formData.poolType?.trim() &&
            formData.length &&
            formData.width &&
            formData.height &&
            formData.shape?.trim() &&
            volume
          );
      
          const isWorkOrderDetailsFilled = Boolean(
            selectedTechnician &&
            selectedTask &&
            date &&
            (selectedTask !== "On Demand" || additionalComments.trim()) &&
            occurrence !== "" &&
            occurrence !== undefined &&
            occurrence !== null
          );
          const finalOccurrence = occurrenceMap[occurrence] ?? 0;
          const customerData = {
            customerDetails: {
              customerId: null,
              companyId: companyId,
              employeeId: selectedTechnician ? selectedTechnician.id : null,
              serviceDay: null,
              custFirstName: formData.firstName,
              custLastName: formData.lastName,
              custEmail: formData.email,
              secondaryEmail: formData.secondaryEmail.trim() || null,
              custAddress: formData.address,
              custCity: formData.city,
              custState: formData.state,
              custZip: formData.zipCode,
              custContactNo: normalizePhoneDigits(formData.phoneNumber),
              currRecInd: formData.status,
              creaTs: null,
              updtTs: null,
            },
            poolDetails: {},
            workOrderDetails: {}, 
          };
      
          if (isPoolDetailsFilled) {
            customerData.poolDetails = {
              company: null,
              companyId: companyId,
              customer: null,
              poolType: formData.poolType,
              poolLength: formData.length,
              poolWidth: formData.width,
              poolHeight: formData.height,
              poolShape: formData.shape,
              poolCapacity: volume,
              creaTs: null,
              updtTs: null,
            };
          }
          if (isWorkOrderDetailsFilled) {
            const finalType = selectedTask === "On Demand" ? "On Demand" : "Service";
            customerData.workOrderDetails = {
              workOrderId: null,
              poolId: null,
              customerId: null,
              employeeId: selectedTechnician ? selectedTechnician.id : null,
              workOrderPart: null,
              workOrderCost: null,
              workOrderComment: additionalComments || null,
              workOrderDueDate: null,
              lastServiceDate: null,
              initialServiceDate: date ? dayjs(date).hour(12).minute(0).second(0).millisecond(0).toISOString() : null,
              statusCode: "Not Started",
              workOrderType: finalType,
              workOrderServiceType: selectedTask || null,
              createdAt: null,
              updatedAt: null,
              workOrderOccurance: finalOccurrence
            };
          }
          await postCustomerOrder(customerData);
          setPopupTitle("Success");
          setPopupMessage("Customer creation successful");
          setPopupVisible(true);
          resetForm();
          // Refresh the customer/employee list to include the newly created customer
          await loadExistingData();
          setTimeout(() => {
            navigate('/dash',  { replace: true });
          }, 2000);
        } catch (error) {
          console.error("Error submitting customer order:", error);
          setPopupTitle("Error");
          const errorMessage = error.response?.data?.message || error.message || "Failed to submit customer order. Please try again.";
          setPopupMessage(errorMessage);
          setPopupVisible(true);
        } finally {
          setIsSubmitting(false);
        }
      };
      

    // Reset form after submission
    const resetForm = () => {
        hasUnsavedDataRef.current = false;
        setStep(1);
        setFormData({
            firstName: "",
            lastName: "",
            phoneNumber: "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
            email: "",
            secondaryEmail: "",
            status: "Y",
            poolType: "",
            length: "",
            width: "",
            height: "",
            shape: "",
            dayOfWeek: "",
            recurrence: "",
        });
        setSelectedTechnician("");
        setSelectedTask("");
        setAdditionalComments("");
        setOccurrence("");
        setDate("");
        setEmailError("");
        setSecondaryEmailError("");
        setSameEmailError("");
    };

    //pool shape options
    const shapeOptions = [
        'Circle',
        'Square',
        'Rectangle',
        'Oval',
        'Kidney',
        'L-Shape',
        'Figure-8',
        'Roman',
        'Freeform',
      ];
    //error function for invalid pool shape
    const validateShape = (inputValue) => {
        if (!shapeOptions.includes(inputValue)) {
          setError(true);
          setErrorMessage('The entered shape does not match our options');
          return false;
        }
        setError(false);
        setErrorMessage('');
        return true;
      };

    // Load existing data function
    const loadExistingData = useCallback(async () => {
        try {
            const [employees, customers] = await Promise.all([
                fetchEmployees(companyId, null, null),
                fetchCustomers(companyId, null, null)
            ]);
            setExistingEmployees(employees || []);
            setExistingCustomers(customers || []);
        } catch (error) {
            console.error('Error fetching existing employees/customers:', error);
        }
    }, [companyId]);

    // Fetch technicians on component mount
    useEffect(() => {
        const loadTechnicians = async () => {
            try {
                const data = await fetchEmployees(companyId, null, "Technician");
                const formattedData = data.map((technicians) => ({
                    id: technicians.employeeId,
                    name: `${technicians.firstName} ${technicians.lastName}`,
                    address: `${technicians.empAddress}, ${technicians.empCity}, ${technicians.empZipCode}`,
                    phone: technicians.contactNumber || '',
                }));
                setTechnicianList(formattedData);
            } catch (error) {
                console.error("Error loading technicians:", error);
            }
        };

        loadTechnicians();
        loadExistingData();
    }, [companyId, loadExistingData]);

    useEffect(() => {
        if (selectedTask === "On Demand") {
            setOccurrence("One time");
        } else if (selectedTask === "Regular Service") {
            setOccurrence("Weekly");
        } else if (selectedTask === "Back-wash") {
            setOccurrence("Quarterly");
        } else if (selectedTask === "Filter breakdown") {
            setOccurrence("Twice a year");
        } else {
            setOccurrence("");
        }
    }, [selectedTask]);

    // Calculate pool volume in gallons
    useEffect(() => {
        const { length, width, height, shape } = formData;
        if (length && width && height && shape) {
            const baseVolume = parseFloat(length) * parseFloat(width) * parseFloat(height) * 7.48;

            // Apply shape-specific multipliers
            let shapeMultiplier = 1.0;
            const shapeLower = shape.toLowerCase();

            if (shapeLower === 'rectangle' || shapeLower === 'square') {
                shapeMultiplier = 1.0;
            } else if (shapeLower === 'oval') {
                shapeMultiplier = 0.89;
            } else if (shapeLower === 'circle') {
                shapeMultiplier = 0.78;
            } else if (shapeLower === 'kidney') {
                shapeMultiplier = 0.75;
            } else if (shapeLower === 'l-shape') {
                shapeMultiplier = 0.85;
            } else if (shapeLower === 'figure-8') {
                shapeMultiplier = 0.75;
            } else if (shapeLower === 'roman') {
                shapeMultiplier = 0.90;
            } else if (shapeLower === 'freeform') {
                shapeMultiplier = 0.80;
            }

            setVolume(baseVolume * shapeMultiplier);
        } else {
            setVolume(null);
        }
    }, [formData]);

    // Handle next step
    const handleNext = () => {
        if (step < 3) setStep(step + 1);
    };

    // Handle previous step
    const handleBack = () => {
        if (step > 1) setStep(step - 1);
    };

    // Update form data
    // Check if email is already used
    const checkEmailDuplicate = (email) => {
        if (!email || !email.trim()) {
            setEmailError("");
            return false;
        }

        const emailLower = email.toLowerCase().trim();

        // Check in employees
        const employeeExists = existingEmployees.some(emp =>
            emp.email && emp.email.toLowerCase().trim() === emailLower
        );

        // Check in customers
        const customerExists = existingCustomers.some(cust =>
            cust.custEmail && cust.custEmail.toLowerCase().trim() === emailLower
        );

        if (employeeExists) {
            setEmailError("This email is already used by an existing employee");
            return true;
        }

        if (customerExists) {
            setEmailError("This email is already used by an existing customer");
            return true;
        }

        setEmailError("");
        return false;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'phoneNumber') {
            // Auto-normalize phone: keep only digits (strip +1, spaces, parens, dashes)
            if (validatePhone(value).validChars) {
                const normalized = normalizePhoneDigits(value);
                setFormData(prev => ({ ...prev, phoneNumber: normalized }));
            }
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));

        if (name === 'email') {
            checkEmailDuplicate(value);
        }
        if (name === 'secondaryEmail') {
            const trimmed = value.trim();
            if (trimmed && !validateEmail(trimmed).isValid) {
                setSecondaryEmailError("Invalid email format");
            } else {
                setSecondaryEmailError("");
            }
            const primaryEmail = (name === 'email' ? value : formData.email).trim().toLowerCase();
            const secEmail = trimmed.toLowerCase();
            if (primaryEmail && secEmail && primaryEmail === secEmail) {
                setSameEmailError("Primary and secondary email must be different.");
            } else {
                setSameEmailError("");
            }
        }

        if (name === 'email') {
            const primaryEmail = value.trim().toLowerCase();
            const secEmail = formData.secondaryEmail.trim().toLowerCase();
            if (primaryEmail && secEmail && primaryEmail === secEmail) {
                setSameEmailError("Primary and secondary email must be different.");
            } else {
                setSameEmailError("");
            }
        }
    };

    useEffect(() => {
        let isValid = false;
        if(step === 1){
            isValid = validatePersonDetails(formData) && !emailError && !secondaryEmailError && !sameEmailError;
        } else if(step === 2){
            const { isValid: poolIsValid, showRequired } = validatePoolDetails(formData, volume);
            isValid = poolIsValid;
            setShowPoolRequired(showRequired);
        } else if(step === 3){
            const { isValid: workOrderIsValid, showRequired, showCommentsRequired } = validateWorkOrderDetails(selectedTechnician, selectedTask, occurrence, date, additionalComments);
            isValid = workOrderIsValid;
            setShowWorkOrderRequired(showRequired);
            setShowCommentsRequired(showCommentsRequired);
        } else {
            isValid = true;
        }
        setIsNextDisabled(!isValid);
    }, [step, formData, selectedTechnician, selectedTask, additionalComments, occurrence, date, volume, emailError, secondaryEmailError, sameEmailError]);

    // Render step content
    const renderStepContent = () => {
        switch (step) {
            case 1:
                return (
                    <div>
                        <Grid container spacing={2}>
                            {/* First Name */}
                            <Grid item xs={6}>
                                <TextField
                                    label="First Name"
                                    name="firstName"
                                    required
                                    fullWidth
                                    value={formData.firstName}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if(validateName(value).validChars) handleChange(e);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText="Only letters and spaces allowed."
                                />
                            </Grid>

                            {/* Last Name */}
                            <Grid item xs={6}>
                                <TextField
                                    label="Last Name"
                                    name="lastName"
                                    required
                                    fullWidth
                                    value={formData.lastName}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if(validateName(value).validChars) handleChange(e);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText="Only letters and spaces allowed."
                                />
                            </Grid>

                            {/* Phone Number */}
                            <Grid item xs={6}>
                                <TextField
                                    label="Phone Number"
                                    name="phoneNumber"
                                    required
                                    fullWidth
                                    value={formData.phoneNumber}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if(validatePhone(value).validChars) handleChange(e);
                                    }}
                                    error={formData.phoneNumber.length > 0 && !validatePhone(formData.phoneNumber).isValid}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText={"10-digit number only."}
                                />
                            </Grid>

                            {/* Address */}
                            <Grid item xs={6}>
                                <TextField
                                    label="Address"
                                    name="address"
                                    required
                                    fullWidth
                                    value={formData.address}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if(validateAddress(value).validChars) handleChange(e);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocationOnIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText="Only letters, numbers, spaces, and # allowed."
                                />
                            </Grid>

                            {/* City */}
                            <Grid item xs={6}>
                            <TextField
                                label="City"
                                name="city"
                                required
                                fullWidth
                                value={formData.city}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if(validateCityState(value).validChars) handleChange(e);
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LocationOnIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                helperText="Only letters and spaces are allowed."
                            />
                            </Grid>

                            {/* State */}
                            <Grid item xs={6}>
                            <Autocomplete
                                options={US_STATES}
                                value={formData.state}
                                onChange={(event, newValue) => {
                                    const syntheticEvent = {
                                        target: {
                                            name: 'state',
                                            value: newValue || '',
                                        },
                                    };
                                    handleChange(syntheticEvent);
                                }}
                                onInputChange={(event, newInputValue, reason) => {
                                    if (reason === 'input') {
                                        const matchByFullName = US_STATES.find(s =>
                                            s.toLowerCase() === newInputValue.toLowerCase()
                                        );
                                        const matchByAbbrev = US_STATES.find(s =>
                                            (STATE_ABBREVS[s] || '').toLowerCase() === newInputValue.toLowerCase()
                                        );
                                        const match = matchByFullName || matchByAbbrev;
                                        if (match) {
                                            handleChange({ target: { name: 'state', value: match } });
                                        }
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="State"
                                        name="state"
                                        required
                                        fullWidth
                                        inputProps={{
                                            ...params.inputProps,
                                            autoComplete: 'address-level1',
                                        }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start">
                                                        <LocationOnIcon />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                        }}
                                        helperText="Select a state from the list."
                                    />
                                )}
                            />
                            </Grid>

                            {/* Zip Code */}
                            <Grid item xs={6}>
                                <TextField
                                    label="Zip Code"
                                    name="zipCode"
                                    required
                                    fullWidth
                                    value={formData.zipCode}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if(validateZip(value).validChars) handleChange(e);
                                    }}
                                    error={formData.zipCode.length > 0 && !validateZip(formData.zipCode).isValid}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <LocationOnIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    helperText="5-digit number only."
                                />
                            </Grid>

                            {/* Email Address */}
                            <Grid item xs={9}>
                                <TextField
                                    label="Email Address"
                                    name="email"
                                    required
                                    fullWidth
                                    value={formData.email}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if(validateEmail(value).validChars) handleChange(e);
                                    }}
                                    onBlur={(e) => {
                                        const value = e.target.value.trim();
                                        if (value && !validateEmail(value).isValid) {
                                            console.error("Invalid email format");
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    error={(formData.email && !validateEmail(formData.email).isValid) || !!emailError}
                                    helperText={
                                        emailError ||
                                        "Enter valid email address, you will receive an email after successful profile creation."
                                    }
                                />
                            </Grid>

                            {/* Secondary Email */}
                            <Grid item xs={9}>
                                <TextField
                                    label="Secondary Email (Optional)"
                                    name="secondaryEmail"
                                    fullWidth
                                    value={formData.secondaryEmail}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if(validateEmail(value).validChars || value === '') handleChange(e);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    error={!!secondaryEmailError || !!sameEmailError}
                                    helperText={sameEmailError || secondaryEmailError || "Optional secondary contact email."}
                                />
                            </Grid>

                           {/* Activity Status as Toggle */}
<Grid item xs={3} >
  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
    <Typography variant="subtitle1" sx={{ minWidth: "60px", fontWeight: 500 }}>
      Status:
    </Typography>
    <FormControlLabel
      control={
        <Switch
          checked={formData.status === "Y"}
          onChange={(e) =>
            setFormData({ ...formData, status: e.target.checked ? "Y" : "N" })
          }
          color="primary"
        />
      }
      label={formData.status === "Y" ? "Active" : "Inactive"}
    />
  </Box>
</Grid>

                        </Grid>
                    </div>
                );
            case 2:
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControl fullWidth>
                                <InputLabel>Pool Type {showPoolRequired && <span style={{ color: "red" }}>*</span>}</InputLabel>
                                <Select
                                    label="Pool Type"
                                    name="poolType"
                                    value={formData.poolType}
                                    onChange={handleChange}
                                    sx={{ "& .MuiInputBase-input": { textAlign: "left" } }}
                                  startAdornment={
                                                      <InputAdornment position="start">
                                                      </InputAdornment>
                                                    }
                                >   
                                    {/* Render dropdown options, added a none option now */}
                                    <MenuItem value="">
                                        <em>None</em> 
                                    </MenuItem>
                                    {poolTypesDD.map((option, index) => (
                                        <MenuItem value={option.value} key={index}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={4}>
                        <TextField
                            label={
                            <>
                                Length (ft) {showPoolRequired && <span style={{ color: "red" }}>*</span>}
                            </>
                            }
                            name="length"
                            fullWidth
                            value={formData.length}
                            onChange={(e) => {
                               const value = e.target.value;
                                    if (/^\d*\.?\d*$/.test(value)) {
                                        handleChange(e);
                                    }
                                }}
                            InputLabelProps={{ shrink: true }}
                            helperText="Enter a number (e.g., 10 or 10.5)."
                        />
                        </Grid>

                        <Grid item xs={4}>
                        <TextField
                            label={
                            <>
                                Width (ft) {showPoolRequired && <span style={{ color: "red" }}>*</span>}
                            </>
                            }
                            name="width"
                            fullWidth
                            value={formData.width}
                            onChange={(e) => {
                            const value = e.target.value;
                                    if (/^\d*\.?\d*$/.test(value)) {
                                        handleChange(e);
                                    }
                                }}
                            InputLabelProps={{ shrink: true }}
                            helperText="Enter a number (e.g., 10 or 10.5)."
                        />
                        </Grid>

                        <Grid item xs={4}>
                        <TextField
                            label={
                            <>
                                Average Depth (ft) {showPoolRequired && <span style={{ color: "red" }}>*</span>}
                            </>
                            }
                            name="height"
                            fullWidth
                            value={formData.height}
                            onChange={(e) => {
                            
                            const value = e.target.value;
                                    if (/^\d*\.?\d*$/.test(value)) {
                                        handleChange(e);
                                    }
                                }}
                            InputLabelProps={{ shrink: true }}
                            helperText="Enter a number (e.g., 10 or 10.5)."
                        />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Typography variant="body1" sx={{ marginTop: "10px", fontWeight: "bold" }}>
                                Calculated Volume:
                            </Typography>
                            <Typography variant="h6" color="primary">
                                {volume !== null ? `${volume.toFixed(2)} gallons` : "-"}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Autocomplete
                                freeSolo
                                options={shapeOptions}
                                value={formData.shape}
                                onInputChange={(event, newInputValue, reason) => {
                                if (reason === 'input') {
                                    if (/^[A-Za-z\s-]*$/.test(newInputValue)) { // Updated regex to allow hyphens
                                    const syntheticEvent = {
                                        target: {
                                        name: 'shape',
                                        value: newInputValue,
                                        },
                                    };
                                    handleChange(syntheticEvent);
                                    validateShape(newInputValue);
                                    }
                                }
                                }}
                                onChange={(event, newValue) => {
                                // When user selects an option from the list
                                const syntheticEvent = {
                                    target: {
                                    name: 'shape',
                                    value: newValue || '',
                                    },
                                };
                                handleChange(syntheticEvent);
                                validateShape(newValue);
                                }}
                                onBlur={() => {
                                validateShape(formData.shape);
                                }}
                                renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={
                                    <>
                                        Shape {showPoolRequired && <span style={{ color: 'red' }}>*</span>}
                                    </>
                                    }
                                    name="shape"
                                    fullWidth
                                    error={error}
                                    helperText={error ? errorMessage : "Enter or choose from the list of shapes."}
                                    InputLabelProps={{ shrink: true }}
                                />
                                )}
                            />
                            </Grid>
                    </Grid>
                );
            case 3:
                return (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="technician-label">Assign Technician {showWorkOrderRequired && <span style={{color: "red"}}>*</span>}</InputLabel>
                                <Select
                                    labelId="technician-label"
                                    value={selectedTechnician ? selectedTechnician.id : ""}
                                    onChange={(event) => {
                                        const tech = technicianList.find(t => t.id === event.target.value);
                                        setSelectedTechnician(tech);
                                    }}
                                    startAdornment={
                                        <InputAdornment position="start">
                                        </InputAdornment>
                                      }
                                    label="Assign Technician"
                                    style={{textAlign: "left"}}
                                >
                                    <MenuItem value="">
                                        <em>None</em> 
                                    </MenuItem>
                                    {technicianList.map((tech) => {
                                        const duplicates = technicianList.filter(t => t.name === tech.name);
                                        const displayName = duplicates.length > 1 && tech.phone
                                            ? `${tech.name} (${tech.phone})`
                                            : tech.name;
                                        return (
                                            <MenuItem key={tech.id} value={tech.id}>
                                                {displayName}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* Task options */}
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={taskOptions}
                                value={selectedTask}
                                onChange={(event, newValue) => setSelectedTask(newValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={
                                            <>Task/Service {showWorkOrderRequired && <span style={{color:"red"}}>*</span>}</>
                                        }
                                        variant="outlined"
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <TaskAltIcon />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                        {/* Further comments */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={
                                    <>Further Comments {showCommentsRequired && <span style={{color:"red"}}>*</span>}</>
                                }
                                value={additionalComments}
                                onChange={(event) => {
                                    if(event.target.value.length <= MAX_COMMENT_LENGTH){
                                        setAdditionalComments(event.target.value);
                                    }
                                }}
                                multiline
                                rows={3}
                                variant="outlined"
                                InputLabelProps={{shrink: true}}
                                helperText={`${MAX_COMMENT_LENGTH - additionalComments.length} characters remaining`}
                            />
                        </Grid>
                        {/* Occurrence */}
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="occurrence-label">Occurrence {showWorkOrderRequired && <span style={{color:"red"}}>*</span>}</InputLabel>
                                <Select
                                    labelId="occurrence-label"
                                    value={occurrence}
                                    onChange={(event) => setOccurrence(event.target.value)}
                                    label="Occurrence"
                                    disabled={selectedTask === "On Demand"}
                                    startAdornment={
                                        <InputAdornment position="start">
                                        </InputAdornment>
                                      }
                                    
                                      style={{textAlign: "left"}}

                                >   
                                    <MenuItem value="">
                                        <em>Clear Selection</em>
                                    </MenuItem>
                                    <MenuItem value="One time" disabled={selectedTask !== "On Demand"}
                                    style={{ display: selectedTask === "On Demand" ? "block" : "none" }}>
                                        One time
                                    </MenuItem>
                                    <MenuItem value="Daily" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Daily</MenuItem>
                                    <MenuItem value="Weekly" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Weekly</MenuItem>
                                    <MenuItem value="Every other week" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Every other week</MenuItem>
                                    <MenuItem value="Once a month" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Once a month</MenuItem>
                                    <MenuItem value="Quarterly" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Quarterly</MenuItem>
                                    <MenuItem value="Twice a year" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Twice a year</MenuItem>
                                    <MenuItem value="Yearly" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Yearly</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {/* Date */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={
                                    <>Service Start Date {showWorkOrderRequired && <span style={{color:"red"}}>*</span>}</>
                                }
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                inputRef={dateInputRef}
                                InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                    <EventIcon
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            if (dateInputRef.current) {
                                                if (dateInputRef.current.showPicker) {
                                                    dateInputRef.current.showPicker();
                                                } else {
                                                    dateInputRef.current.focus();
                                                }
                                            }
                                        }}
                                    />
                                    </InputAdornment>
                                ),
                                }}
                                InputLabelProps={{
                                shrink: true, // Ensures the label doesn't overlap with the value
                                }}
                                inputProps={{
                                min: dayjs().format("YYYY-MM-DD"),  // Restrict past dates
                                }}
                                variant="outlined"
                            />
                            </Grid>
                    </Grid>
                );
            default:
                return null;
        }
    };

    return (
        <Box sx={{ padding: "20px", paddingTop: "120px", paddingBottom: "40px" }}>
            <Box sx={{ maxWidth: "900px", margin: "0 auto" }}>
                {/* Breadcrumbs */}
                <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" />}
                    aria-label="breadcrumb"
                    sx={{ marginBottom: "20px", paddingLeft: "10px" }}
                >
                    <Link
                        underline="hover"
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        color="inherit"
                        onClick={() => navigate('/dash')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        Home
                    </Link>
                    <Link
                        underline="hover"
                        color={step === 1 ? "text.primary" : "inherit"}
                        sx={{
                            cursor: step > 1 ? 'pointer' : 'default',
                            fontWeight: step === 1 ? 'bold' : 'normal'
                        }}
                        onClick={() => step > 1 && setStep(1)}
                    >
                        Customer Details
                    </Link>
                    <Link
                        underline="hover"
                        color={step === 2 ? "text.primary" : "inherit"}
                        sx={{
                            cursor: step > 2 ? 'pointer' : 'default',
                            fontWeight: step === 2 ? 'bold' : 'normal'
                        }}
                        onClick={() => step > 2 && setStep(2)}
                    >
                        Pool Details
                    </Link>
                    <Typography
                        color={step === 3 ? "text.primary" : "text.disabled"}
                        sx={{ fontWeight: step === 3 ? 'bold' : 'normal' }}
                    >
                        Assign Technician
                    </Typography>
                </Breadcrumbs>

                <Paper elevation={3} sx={{ padding: "20px", textAlign: "center" }}>
                <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: "20px" }}>
                    {step === 1 ? "Add Customer Details" : step === 2 ? "Add Pool Details" : "Assign Technician"}
                </Typography>
                {renderStepContent()}
                <Box sx={{ marginTop: "20px", display: "flex", justifyContent: "space-between" }}>
                    {step === 1 ? (
                        <Button variant="outlined" onClick={() => navigate(-1)} disabled={isSubmitting}>
                            Cancel
                        </Button>
                    ) : (
                        <Button variant="outlined" onClick={handleBack} disabled={isSubmitting}>
                            Back
                        </Button>
                    )}
                    <Tooltip title={isNextDisabled ? "Please fill all required (*) fields before proceeding" : ""} arrow>
                        <span>
                            <Button
                                variant="contained"
                                onClick={step === 3 ? handleSubmit : handleNext}
                                disabled={isNextDisabled || isSubmitting}
                                startIcon={isSubmitting ? <span style={{display:'inline-block',width:16,height:16,border:'2px solid #fff',borderTop:'2px solid transparent',borderRadius:'50%',animation:'spin 0.8s linear infinite'}} /> : null}
                            >
                                {isSubmitting ? "Submitting..." : step === 3 ? "Submit" : "Next"}
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Paper>

            {/* Popup Modal for Alerts */}
            <PopupGeneric
                isVisible={isPopupVisible}
                onClose={() => setPopupVisible(false)}
                title={popupTitle}
                content={<p>{popupMessage}</p>}
                actions={
                    <button
                        onClick={() => setPopupVisible(false)}
                        style={{
                            marginTop: '10px',
                            padding: '5px 10px',
                            borderRadius: '5px',
                            backgroundColor: '#003366',
                            color: '#fff',
                            border: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        OK
                    </button>
                }
            />

            {/* Navigation Blocker Confirmation Popup */}
            <PopupGeneric
                isVisible={isNavigationBlockerVisible}
                onClose={() => {}}
                title="Unsaved Changes"
                content={
                    <div>
                        <p>You have unsaved customer details.</p>
                        <p>If you leave, all entered information will be lost.</p>
                    </div>
                }
                actions={
                    <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
                        <button
                            onClick={() => {
                                hasUnsavedDataRef.current = false;
                                if (blockedNavigation) {
                                    blockedNavigation.proceed();
                                    setNavigationBlockerVisible(false);
                                }
                            }}
                            style={{
                                padding: "8px 16px",
                                borderRadius: "5px",
                                backgroundColor: "#8f0c03",
                                color: "#fff",
                                border: "none",
                                cursor: "pointer",
                            }}
                        >
                            Leave
                        </button>
                        <button
                            onClick={() => {
                                if (blockedNavigation) {
                                    blockedNavigation.reset();
                                    setNavigationBlockerVisible(false);
                                }
                            }}
                            style={{
                                padding: "8px 16px",
                                borderRadius: "5px",
                                backgroundColor: "#1d5ca7",
                                border: "none",
                                color: "#fff",
                                cursor: "pointer",
                            }}
                        >
                            Stay on Page
                        </button>
                    </div>
                }
            />
            </Box>
        </Box>
    );
};

export default AddCustomer;